import NumberLabel from 'components/commons/label/NumberLabel';
import { getIconSrcFromRefKey, TARGET_GOAL_REF_KEY } from 'hooks/useGrouping';
import { useRouting } from 'hooks/useRouting';
import React from 'react';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { getSignColor } from 'components/features/new-report/yearly-report/YearlyReport';
import { PeriodGoalItemProps } from 'components/features/home/assetStuffing/TotalAssetStuffingFluctuationList';
import { floorByUnit, Floored, getUnit, Unit } from '../../utils/constructor';
import {
  addSignCommaFormat,
  commaFormat,
  CommaFormattedString,
  getSign,
  Sign,
  SignAddedCommaFormattedString,
} from '../../utils/formatter';
import { getPeriodGoal } from 'config/api';
import { useQuery } from 'react-query';
import Loading from 'components/commons/loading/Loading';

export type TargetAchievementListProps = {
  targetAchievementData: Omit<TargetAchivementProps, 'month'>[];
  userName: string;
  month: number;
};

const TARGET_ACHIEVEMENT_STATE = {
  CLOSE: 'CLOSE',
  ACHIEVED: 'ACHIEVED',
  NOT_ACHIEVED: 'NOT_ACHIEVED',
} as const;
type TargetAchievement =
  | {
      state: typeof TARGET_ACHIEVEMENT_STATE.CLOSE;
      achievement: '---';
      targetAmount: '---';
    }
  | {
      state: typeof TARGET_ACHIEVEMENT_STATE.ACHIEVED;
      achievementId: string;
      achievement: SignAddedCommaFormattedString;
      achievementSign: Sign;
      targetAmount: CommaFormattedString;
      achievementRate: number;
    }
  | {
      state: typeof TARGET_ACHIEVEMENT_STATE.NOT_ACHIEVED;
      achievementId: string;
      achievement: SignAddedCommaFormattedString;
      achievementSign: Sign;
      targetAmount: CommaFormattedString;
      achievementRate: number;
    };
type TargetAchivementProps = {
  targetAchievement: TargetAchievement;
  iconSrc: string;
  targetTitle: string;
  entireTargetAmount: CommaFormattedString & Floored;
  entireTargetAmountUnit: Unit;
  completed: boolean;
  month: number;
};

type ChartProps = {
  achievementRate: number | null;
  state: typeof TARGET_ACHIEVEMENT_STATE[keyof typeof TARGET_ACHIEVEMENT_STATE];
};
type CircleChartProps = {
  Chart: React.FC<ChartProps>;
  chartData: ChartProps;
};

type AchievementRateProps = {
  achievementRate: number | null;
};

type NoTargetProps = {
  userName: string;
};
export type TitleProps = { month: number };
type KeyProps = { myKey: number };

const TargetAchivement: React.FC<TargetAchivementProps & KeyProps> = ({
  targetAchievement,
  iconSrc,
  targetTitle,
  entireTargetAmount,
  entireTargetAmountUnit,
  completed,
  month,
  myKey,
}) => {
  const { achievement, targetAmount, state } = targetAchievement;
  const { pushRouting } = useRouting();

  const isClose = (
    targetAchievement: TargetAchievement
  ): targetAchievement is {
    state: typeof TARGET_ACHIEVEMENT_STATE.CLOSE;
    achievement: '---';
    targetAmount: '---';
  } => targetAchievement.state === TARGET_ACHIEVEMENT_STATE.CLOSE;

  const chartData = {
    achievementRate:
      targetAchievement.state === 'CLOSE'
        ? null
        : targetAchievement.achievementRate,
    state,
  };
  const linkClickHandler = (): void => {
    if (!isClose(targetAchievement) && !completed)
      pushRouting({ path: `/target/${targetAchievement.achievementId}` }, true);
  };

  return (
    <>
      <li
        className={`flex items-center border border-bluegray-200 rounded-7 cursor-pointer ${
          myKey !== 0 ? 'mt-4' : ''
        } ${isClose(targetAchievement) ? 'is-close' : ''}`}
        onClick={linkClickHandler}>
        <div className="flex-1">
          <div className="flex items-center p-4 border-b border-bluegray-200">
            <div style={{ width: '46px', height: '46px' }}>
              <img src={iconSrc} alt="" />
            </div>
            <div className="flex-1 pl-3 text-bluegray-700">
              <div className="text-base font-bold mb-2">{targetTitle}</div>
              <div className="text-xs text-right">
                目標金額：{entireTargetAmount}
                {entireTargetAmountUnit}円
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="mb-2 text-base font-bold text-bluegray-700">
              {month}月の達成状況
            </div>
            <div className="flex justify-between">
              <div className="flex items-center flex-1">
                <div className="mr-3" style={{ width: '66px', height: '66px' }}>
                  <CircleChart Chart={Chart} chartData={chartData} />
                </div>
                <AchievementRate achievementRate={chartData.achievementRate} />
              </div>
              <div className="flex-1 text-right">
                <div className="flex items-center justify-end">
                  <div className="mr-2 text-1.5xs text-bluegray-550">実績</div>
                  <div
                    className={`text-base font-bold ${
                      isClose(targetAchievement)
                        ? ''
                        : getSignColor(targetAchievement.achievementSign)
                    }`}>
                    {achievement}円
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-end">
                  <img
                    src="/static/images/report/symbol-slash.svg"
                    className="symbol-slash"
                    alt=""
                  />
                  <div className="mr-2 text-1.5xs text-bluegray-550">目標</div>
                  <div className="text-sm font-bold text-bluegray-500">
                    {targetAmount}円
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    </>
  );
};

export const Chart: React.FC<ChartProps> = ({ achievementRate, state }) => {
  const getInnerMessage = (
    state: typeof TARGET_ACHIEVEMENT_STATE[keyof typeof TARGET_ACHIEVEMENT_STATE]
  ): string => {
    switch (state) {
      case TARGET_ACHIEVEMENT_STATE.CLOSE:
        return '終了';
      case TARGET_ACHIEVEMENT_STATE.ACHIEVED:
        return '達成';
      case TARGET_ACHIEVEMENT_STATE.NOT_ACHIEVED:
        return '未達';
    }
  };
  const message = getInnerMessage(state);
  const isOnTheWayAchieving = (
    achievementRate: number | null
  ): achievementRate is number =>
    achievementRate !== null && state === TARGET_ACHIEVEMENT_STATE.NOT_ACHIEVED;

  return (
    <div
      className={`circle-graph circle-graph-single ${
        state === TARGET_ACHIEVEMENT_STATE.ACHIEVED ? 'status-success' : ''
      }`}
      style={{
        width: '100%',
        height: '100%',
        backgroundImage: isOnTheWayAchieving(achievementRate)
          ? `conic-gradient(#3B82F6 0%, ${achievementRate *
              100}%, transparent 0%, transparent 100%)`
          : '',
      }}>
      <div className="circle-graph-inner">
        <div className="circle-graph-secondary-value-row">
          <div
            className="circle-graph-secondary-value"
            style={{ marginLeft: '0px', marginTop: '15px' }}>
            {message}
          </div>
        </div>
      </div>
    </div>
  );
};

const CircleChart: React.FC<CircleChartProps> = ({ Chart, chartData }) => {
  const { achievementRate, state } = chartData;
  return <Chart achievementRate={achievementRate} state={state} />;
};

const AchievementRate: React.FC<AchievementRateProps> = ({
  achievementRate,
}) => {
  const rate = achievementRate ? Math.floor(achievementRate * 100) : null;
  return (
    <>
      <NumberLabel
        classNames="text-lg"
        value={rate === null || rate < 0 ? null : rate > 100 ? 100 : rate}
        noValueText="0"
      />
      <span className="">%</span>
    </>
  );
};

const NoTarget: React.FC<NoTargetProps> = ({ userName }) => {
  const link = '/target';
  return (
    <>
      <div className="bg-white">
        <div className="p-4 overflow-hidden">
          <div className="p-6 text-sm text-center text-bluegray-500 bg-bluegray-50 border border-bluegray-200 rounded-7">
            現在登録されている目標はありません
          </div>
        </div>
        <div className="p-4 overflow-hidden">
          <div className="mb-3 text-sm font-bold text-center">
            目標を登録しませんか？
          </div>
          <img
            src="/static/images/report/target-setting-banner.png"
            className="my-0 mx-auto"
            alt=""
          />
          <div className="p-4 overflow-hidden">
            <div className="text-sm">
              「資産目標サポート」で{userName}
              さんの資産運用の目標を設定してみませんか？
              目標達成のために「毎月いくら増やす必要があるのか」が明確になり、資産運用がますます分かりやすくなります。
            </div>
            <div className="w-full flex items-center p-0 mt-4">
              <a
                className="h-5 flex items-center ml-auto text-sm text-blue-700"
                href={link}>
                <div className="icon-right"></div>
                資産目標サポートで目標を登録する
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const ToAdviceTop: React.FC<Record<string, unknown>> = () => {
  const link = '/target';
  return (
    <>
      <div className="p-4 overflow-hidden">
        <div className="w-full flex items-center">
          <a
            className="h-5 flex items-center ml-auto text-sm text-blue-700"
            href={link}>
            <div className="icon-right"></div>
            資産目標サポートTOPへ
          </a>
        </div>
      </div>
    </>
  );
};

const Title: React.FC<TitleProps> = ({ month }) => (
  <div className="h-22.5 flex items-end p-4 text-lg font-bold text-bluegray-700 leading-tight">
    <img
      src="/static/images/report/icon-flag.svg"
      alt=""
      className="h-4 w-4 mr-2"
    />
    {month}月の目標達成状況
  </div>
);

export const TargetAchievementList = ({
  from,
  queryPeriod,
  userName,
}: {
  from: number;
  queryPeriod: {
    from: string;
    to: string;
  };
  userName: string;
}) => {
  const { data: goalsData } = useQuery(
    'monthlyGoals',
    () => fetchGoals(queryPeriod.from, queryPeriod.to),
    {
      refetchOnWindowFocus: false,
    }
  );

  const month = (yyyymmddToDate(from.toString()) || new Date()).getMonth() + 1;

  if (goalsData === null || goalsData === undefined) return <LoadingData />;
  const goals = goalsData as PeriodGoalItemProps[];

  const targetAchievementData = goals.map(data => {
    const achievementProgressRate =
      data.achievementAmount === null || data.targetAmount === null
        ? 0
        : (data.achievementAmount / data.targetAmount) * 100;
    const state =
      data.achievementAmount === null || data.targetAmount === null
        ? TARGET_ACHIEVEMENT_STATE.CLOSE
        : achievementProgressRate >= 100
        ? TARGET_ACHIEVEMENT_STATE.ACHIEVED
        : TARGET_ACHIEVEMENT_STATE.NOT_ACHIEVED;
    const targetAchievement: TargetAchievement =
      data.achievementAmount === null ||
      data.targetAmount === null ||
      state === TARGET_ACHIEVEMENT_STATE.CLOSE
        ? {
            state: TARGET_ACHIEVEMENT_STATE.CLOSE,
            achievement: '---',
            targetAmount: '---',
          }
        : {
            state,
            achievementId: data.id,
            achievement: addSignCommaFormat(data.achievementAmount),
            achievementSign: getSign(data.achievementAmount),
            targetAmount: commaFormat(data.targetAmount),
            achievementRate:
              data.targetAmount !== 0
                ? data.achievementAmount / data.targetAmount
                : 0,
          };
    const iconSrc = getIconSrcFromRefKey(
      data.refType as typeof TARGET_GOAL_REF_KEY[keyof typeof TARGET_GOAL_REF_KEY]
    );
    const targetTitle = data.title;
    const entireTargetAmountUnit = getUnit(data.totalTargetAmount);
    const entireTargetAmount = commaFormat(
      floorByUnit(data.totalTargetAmount, entireTargetAmountUnit)
    );
    return {
      targetAchievement,
      iconSrc,
      targetTitle,
      entireTargetAmount,
      entireTargetAmountUnit,
      completed: data.completed,
    };
  });

  const isEmpty = targetAchievementData.length === 0;

  return (
    <>
      <Title month={month} />
      {isEmpty && <NoTarget userName={userName} />}
      <div className="bg-white">
        <ul className="pt-6 px-4">
          {targetAchievementData.map((p, i) => {
            return <TargetAchivement {...p} myKey={i} month={month} />;
          })}
        </ul>
        <ToAdviceTop />
      </div>
    </>
  );
};

const LoadingData = () => {
  return (
    <>
      <div className="h-22.5 flex items-end p-4 text-lg font-bold text-bluegray-700 leading-tight">
        <img
          src="/static/images/report/icon-flag.svg"
          alt=""
          className="h-4 w-4 mr-2"
        />
        --月の目標達成状況
      </div>
      <div className="bg-white">
        <Loading
          loading={true}
          circularLoading={true}
          style={{
            paddingTop: '60px',
            paddingBottom: '60px',
          }}
        />
      </div>
    </>
  );
};
const fetchGoals = async (from: string, to: string) => {
  const res_ = await getPeriodGoal(from, to);
  const res = res_.Items.map(item => {
    return {
      id: item.ID,
      refType: item.RefType,
      title: item.Title,
      totalTargetAmount: item.TotalTargetAmount,
      targetAmount: item.TargetAmount,
      achievementAmount: item.AchievementAmount,
      completed: item.Completed,
    };
  });
  return res;
};
